import { z } from 'zod';
import { CRITTER_KINDS, type CritterKind } from '../sim/critters';
import { Dir, type Direction } from '../sim/direction';
import {
  ArtAxis,
  PICKUP_KINDS,
  validateSimLevel,
  type ArtSpec,
  type HerdSpec,
  type RivalSpec,
  type PickupKind,
  type SimLevel,
  type SoccerSpec,
} from '../sim/level';
import { parseAsciiMap, regrowFor } from './asciiMap';
import { BIOMES, type BiomeId } from './biomes';
import { MOWER_IDS, MOWERS, type MowerId } from './mowers';
import type { TileId } from './tiles';

/**
 * Level file format (prototype subset of docs/ARCHITECTURE.md §4). Hand-built levels are authored
 * as ASCII rows; generated levels (Phase 2) will add seed/generatorVersion/par/pickups/entities.
 */

const DIRECTION_LETTERS = { N: Dir.N, E: Dir.E, S: Dir.S, W: Dir.W } as const;

const MowerIdSchema = z.enum(MOWER_IDS as [MowerId, ...MowerId[]]);

export const MowerRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('minTier'), tier: z.number().int().min(0) }),
  z.object({ kind: z.literal('only'), mowers: z.array(MowerIdSchema).min(1) }),
  z.object({ kind: z.literal('exclude'), mowers: z.array(MowerIdSchema).min(1) }),
]);
export type MowerRule = z.infer<typeof MowerRuleSchema>;

export const LevelFileSchema = z.object({
  id: z.string().regex(/^[a-z0-9-]+$/, 'lowercase id with dashes'),
  name: z.string().min(1),
  seed: z.string().optional(),
  /** Generated levels: which generator produced them, for which difficulty, and the par times. */
  generatorVersion: z.number().int().min(1).optional(),
  level: z.number().int().min(1).optional(),
  biome: z.enum(BIOMES.map((b) => b.id) as [BiomeId, ...BiomeId[]]).optional(),
  /** Autopilot clear time in ticks, per allowed mower. */
  par: z.partialRecord(MowerIdSchema, z.number().int().min(1)).optional(),
  /** Late lawns: the fastest route found with smashing allowed, per mower (the gold time). */
  goldTime: z.partialRecord(MowerIdSchema, z.number().int().min(1)).optional(),
  /** Which route try gave each par and gold time (src/meta/par.ts), so tests can replay it. */
  routeTries: z
    .object({
      par: z.partialRecord(MowerIdSchema, z.number().int().min(0)),
      gold: z.partialRecord(MowerIdSchema, z.number().int().min(0)).optional(),
    })
    .optional(),
  startDir: z.enum(['N', 'E', 'S', 'W']),
  mowerRule: MowerRuleSchema,
  /** Map rows; see src/content/asciiMap.ts for the legend. Must contain one `S`. */
  rows: z.array(z.string().min(1)).min(1),
  pickups: z
    .array(
      z.object({
        kind: z.enum(PICKUP_KINDS as [PickupKind, ...PickupKind[]]),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
      }),
    )
    .optional(),
  critters: z
    .array(
      z.object({
        kind: z.enum(CRITTER_KINDS as [CritterKind, ...CritterKind[]]),
        x: z.number().int().min(0),
        y: z.number().int().min(0),
      }),
    )
    .optional(),
  /** Boss: Lawn Art (see SimLevel.art). Rows: '-' across, '|' along, '.' no target. */
  art: z
    .object({
      pattern: z.array(z.string().regex(/^[-|.]+$/)).min(1),
      stars: z.tuple([z.number().min(0).max(1), z.number().min(0).max(1)]),
    })
    .optional(),
  /** Moles pushing up molehills to flatten (see SimLevel.moles). */
  moles: z
    .object({
      hills: z.number().int().min(0),
      first: z.number().int().min(1),
      every: z.number().int().min(1),
    })
    .optional(),
  /** Hills (see SimLevel.hills): a mound in each rectangle. */
  hills: z
    .array(
      z.object({
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        w: z.number().int().min(3),
        h: z.number().int().min(3),
      }),
    )
    .optional(),
  /** Stray balls (see SimLevel.balls). */
  balls: z.object({ first: z.number().int().min(1), every: z.number().int().min(1) }).optional(),
  /**
   * Boss: mower soccer (see SimLevel.soccer). The goal mouths are the middle `mouth` tiles of the
   * top and bottom rows; the center spot is the middle of the pitch.
   */
  soccer: z
    .object({
      target: z.number().int().min(1),
      halfTiles: z.number().int().min(1),
      mouth: z.number().int().min(1).optional(),
      stars: z.object({ goals: z.number().int().min(1), mowed: z.number().min(0).max(1) }),
    })
    .optional(),
  /** Boss: Hedge Maze (see SimLevel.party). */
  party: z.object({ tiles: z.number().int().min(1) }).optional(),
  /** Boss: Lights Out (see SimLevel.dark). */
  dark: z.boolean().optional(),
  /** Falling trees (see SimLevel.falls): each a dead tree tile in the map. */
  falls: z
    .array(
      z.object({
        x: z.number().int().min(0),
        y: z.number().int().min(0),
        dir: z.enum(['N', 'E', 'S', 'W']),
        at: z.number().int().min(1),
        length: z.number().int().min(1).max(4),
      }),
    )
    .optional(),
  /** Boss: Rival Robo-Mower (see SimLevel.rival). The robot's dock ('D') is in the map. */
  rival: z
    .object({
      dir: z.enum(['N', 'E', 'S', 'W']),
      speed: z.number().min(1),
      target: z.number().min(0).max(1),
    })
    .optional(),
  /** Boss: Round Up the Sheep (see SimLevel.herd). The pen ('P') and gate ('G') are in the map. */
  herd: z.literal(true).optional(),
  /** Boss: Beat the Storm (see SimLevel.storm). */
  storm: z
    .object({
      delayTiles: z.number().min(0),
      pace: z.number().min(1),
      target: z.number().min(0).max(1),
      accel: z.number().min(0).optional(),
      minPace: z.number().min(1).optional(),
    })
    .optional(),
});
export type LevelFile = z.infer<typeof LevelFileSchema>;

export interface LevelDef {
  id: string;
  name: string;
  mowerRule: MowerRule;
  sim: SimLevel;
  /** Content art per tile (row-major), for rendering. */
  art: TileId[];
  /** Autopilot clear time in ticks, per allowed mower (generated levels). */
  par: Partial<Record<MowerId, number>>;
  /** Late lawns: the gold time's route, per mower (src/meta/par.ts). */
  goldTime?: Partial<Record<MowerId, number>>;
  /** Which route try gave each par and gold time (src/meta/par.ts). */
  routeTries?: LevelFile['routeTries'];
  /** Difficulty level, for generated levels. */
  level?: number;
  /** The world it is set in, when the file says (a builder lawn's yard). */
  biome?: BiomeId;
}

/** Parses and validates a level file. Throws with a readable message on any problem. */
export function loadLevel(json: unknown): LevelDef {
  const file = LevelFileSchema.parse(json);
  const map = parseAsciiMap(file.rows);
  if (!map.start) throw new Error(`${file.id}: map has no start marker 'S'`);
  const dir: Direction = DIRECTION_LETTERS[file.startDir];
  const sim: SimLevel = {
    id: file.id,
    seed: file.seed ?? file.id,
    width: map.width,
    height: map.height,
    tiles: map.tiles,
    toughness: map.toughness,
    start: { ...map.start, dir },
    ...regrowFor(map.art, map.width),
    pickups: file.pickups ?? [],
    critters: file.critters ?? [],
    ...(file.storm ? { storm: file.storm } : {}),
    ...(file.moles ? { moles: file.moles } : {}),
    ...(file.falls
      ? { falls: file.falls.map((f) => ({ ...f, dir: DIRECTION_LETTERS[f.dir] })) }
      : {}),
    ...(file.dark ? { dark: true } : {}),
    ...(file.party ? { party: file.party } : {}),
    ...(file.balls ? { balls: file.balls } : {}),
    ...(file.soccer ? { soccer: soccerSpec(file.soccer, map.width, map.height) } : {}),
    ...(file.hills ? { hills: file.hills } : {}),
    ...(file.art ? { art: artSpec(file.id, file.art, map.art, map.width) } : {}),
    ...(file.herd ? { herd: herdSpec(file.id, map.art, map.width) } : {}),
    ...(file.rival ? { rival: rivalSpec(file.id, file.rival, map.art, map.width) } : {}),
  };
  validateSimLevel(sim);
  return {
    id: file.id,
    name: file.name,
    mowerRule: file.mowerRule,
    sim,
    art: map.art,
    par: file.par ?? {},
    ...(file.goldTime ? { goldTime: file.goldTime } : {}),
    ...(file.routeTries ? { routeTries: file.routeTries } : {}),
    ...(file.level !== undefined ? { level: file.level } : {}),
    ...(file.biome !== undefined ? { biome: file.biome } : {}),
  };
}

/** Mower soccer: goal mouths in the middle of the top and bottom rows, the spot in the center. */
export function soccerSpec(
  soccer: NonNullable<LevelFile['soccer']>,
  width: number,
  height: number,
): SoccerSpec {
  const mouth = soccer.mouth ?? SOCCER_MOUTH;
  const x0 = Math.floor((width - mouth) / 2);
  const mouths = Array.from({ length: mouth }, (_, i) => [
    { x: x0 + i, y: 0 },
    { x: x0 + i, y: height - 1 },
  ]).flat();
  return {
    mouths,
    center: { x: Math.floor(width / 2), y: Math.floor(height / 2) },
    target: soccer.target,
    halfTiles: soccer.halfTiles,
    stars: soccer.stars,
  };
}

/** Mower soccer: how many tiles wide a goal mouth is. */
export const SOCCER_MOUTH = 4;

/** A lawn-art picture from its rows; the goal is the judges' table in the map, if it has one. */
function artSpec(
  id: string,
  art: NonNullable<LevelFile['art']>,
  tiles: readonly TileId[],
  width: number,
): ArtSpec {
  const pattern = art.pattern.flatMap((row) =>
    [...row].map((c) => (c === '-' ? ArtAxis.Across : c === '|' ? ArtAxis.Along : ArtAxis.None)),
  );
  if (pattern.length !== tiles.length)
    throw new Error(`${id}: art pattern size does not match the map`);
  const table = tiles.indexOf('judgesTable');
  const goal = table < 0 ? {} : { goal: { x: table % width, y: Math.floor(table / width) } };
  return { pattern, ...goal, stars: art.stars };
}

/** A herding lawn's pen and gate, from the map. */
function herdSpec(id: string, tiles: readonly TileId[], width: number): HerdSpec {
  const at = (i: number) => ({ x: i % width, y: Math.floor(i / width) });
  const pen = tiles.flatMap((t, i) => (t === 'pen' ? [at(i)] : []));
  const gate = tiles.indexOf('penGate');
  if (pen.length === 0 || gate < 0)
    throw new Error(`${id}: herding needs a pen ('P') and gate ('G')`);
  return { pen, gate: at(gate) };
}

/** A rival lawn's robot, docked where the map has its dock. */
function rivalSpec(
  id: string,
  rival: NonNullable<LevelFile['rival']>,
  tiles: readonly TileId[],
  width: number,
): RivalSpec {
  const dock = tiles.indexOf('roboDock');
  if (dock < 0) throw new Error(`${id}: the rival needs a dock ('D')`);
  return {
    start: { x: dock % width, y: Math.floor(dock / width), dir: DIRECTION_LETTERS[rival.dir] },
    speed: rival.speed,
    target: rival.target,
  };
}

/** Mowers a level permits, in roster order. */
export function allowedMowers(rule: MowerRule): MowerId[] {
  switch (rule.kind) {
    case 'minTier':
      return MOWER_IDS.filter((id) => MOWERS[id].tier >= rule.tier);
    case 'only':
      return MOWER_IDS.filter((id) => rule.mowers.includes(id));
    case 'exclude':
      return MOWER_IDS.filter((id) => !rule.mowers.includes(id));
  }
}
